import { useEffect, useRef, useState } from "react";
import api from "../api";
import "./ReportsPage.css";
import { usePermissions } from "../hooks/usePermissions";
import { printHtml } from "../utils/print";

type Period = "today" | "week" | "month" | "last_month" | "quarter" | "year";
type Tab = "overview" | "finance" | "warehouse" | "halyk" | "sales" | "staff" | "earnings";

// ── Types ──────────────────────────────────────────────────────────
interface DealItem {
  dealId: number; dealNumber: string; orderCode: string;
  clientName: string; clientPhone: string; products: string;
  amount: number; commissionRate: number; commission: number;
  montCost: number; notifCost: number; notifChannel: string;
  preTax: number; tax: number; netProfit: number;
  paidAt: string; activationKeys: string[];
}
interface HalykReport {
  period: string; dealsCount: number; revenue: number;
  totalCommission: number; totalMontCost: number; totalNotifCost: number;
  totalPreTax: number; totalTax: number; totalNetProfit: number;
  commissionRate: number; deals: DealItem[];
}
interface SalesRow {
  productId: number; nameRu: string; sku: string | null; category: string;
  qty: number; dealsCount: number;
  revenue: number; commission: number; fixedCosts: number;
  preTax: number; tax: number; cost: number; profit: number;
}
interface SalesReport {
  period: string;
  rows: SalesRow[];
  totals: { qty: number; revenue: number; commission: number; fixedCosts: number; preTax: number; tax: number; cost: number; profit: number };
}
interface Channel {
  id: number; name: string; dealsCount: number; revenue: number;
  montCost: number; commission: number; notifCost: number; tax: number; netProfit: number;
}
interface OverviewReport {
  totalDeals: number; totalRevenue: number; totalMontCost: number;
  totalHalykComm: number; totalNotifCost: number; totalFixed: number;
  totalPreTax: number; totalTax: number; totalNetProfit: number;
  channels: Channel[];
}
interface MonthlyTrend { month: string; revenue: number; }
interface FinanceReport {
  totalRevenue: number; totalDeals: number; avgTicket: number;
  totalMontCost: number; grossProfit: number; grossMargin: number;
  totalHalykComm: number; totalNotifCost: number; totalFixed: number;
  notifFixed: number; serverFixed: number; phoneFixed: number; totalExpenses: number;
  ebit: number; tax: number; netProfit: number; netMargin: number;
  lostDealsCount: number; activeDealsCount: number; pipelineRevenue: number;
  monthlyTrend: MonthlyTrend[];
}
interface Category { id: number; name: string; }

interface SupplierRow { id: number | null; name: string; docsCount: number; amount: number; }
interface ProductRow  { id: number; name: string; sku: string; qty: number; amount: number; }
interface IssueReason { reason: string; qty: number; amount: number; docsCount: number; }
interface WarehouseReport {
  receiptsCount: number; totalReceiptAmount: number; totalReceiptQty: number;
  issuesCount: number; totalIssueAmount: number; totalIssueQty: number;
  suppliers: SupplierRow[];
  topReceiptProducts: ProductRow[];
  issueReasons: IssueReason[];
}

const fmt = (n: number) => Math.round(n).toLocaleString("ru-RU") + " ₸";
const fmtN = (n: number) => Math.round(n).toLocaleString("ru-RU");
const pct = (n: number) => n.toFixed(1) + "%";

const PERIODS: { key: Period; label: string }[] = [
  { key: "today",      label: "Сегодня" },
  { key: "week",       label: "Неделя" },
  { key: "month",      label: "Месяц" },
  { key: "last_month", label: "Прошлый мес." },
  { key: "quarter",    label: "Квартал" },
  { key: "year",       label: "Год" },
];

export default function ReportsPage() {
  const perms = usePermissions();
  const defaultTab: Tab = perms.canSeeFinanceReports ? "overview" : "earnings";
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [period, setPeriod] = useState<Period>("month");

  const contentRef = useRef<HTMLDivElement>(null);
  const [printAll, setPrintAll] = useState(false);

  // Staff
  const [staff, setStaff] = useState<any | null>(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffFilter, setStaffFilter] = useState<number | "">("");

  // Earnings (заработок)
  const [earn, setEarn] = useState<any | null>(null);
  const [earnLoading, setEarnLoading] = useState(false);
  const [openEmp, setOpenEmp] = useState<number | null>(null);

  // Overview
  const [overview, setOverview] = useState<OverviewReport | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Finance
  const [finance, setFinance] = useState<FinanceReport | null>(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // Halyk
  const [halyk, setHalyk] = useState<HalykReport | null>(null);
  const [halykLoading, setHalykLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Warehouse
  const [warehouse, setWarehouse] = useState<WarehouseReport | null>(null);
  const [warehouseLoading, setWarehouseLoading] = useState(false);

  // Sales
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [sales, setSales] = useState<SalesReport | null>(null);
  const [salesLoading, setSalesLoading] = useState(false);

  useEffect(() => {
    if (tab === "overview") {
      setOverviewLoading(true);
      api.get(`/reports/overview?period=${period}`)
        .then(r => setOverview(r.data))
        .finally(() => setOverviewLoading(false));
    }
  }, [tab, period]);

  useEffect(() => {
    if (tab === "finance") {
      setFinanceLoading(true);
      api.get(`/reports/finance?period=${period}`)
        .then(r => setFinance(r.data))
        .finally(() => setFinanceLoading(false));
    }
  }, [tab, period]);

  useEffect(() => {
    if (tab === "warehouse") {
      setWarehouseLoading(true);
      api.get(`/reports/warehouse?period=${period}`)
        .then(r => setWarehouse(r.data))
        .finally(() => setWarehouseLoading(false));
    }
  }, [tab, period]);

  useEffect(() => {
    if (tab === "halyk") {
      setHalykLoading(true);
      api.get(`/reports/halyk-market?period=${period}`)
        .then(r => setHalyk(r.data))
        .finally(() => setHalykLoading(false));
    }
  }, [tab, period]);

  useEffect(() => {
    if (tab === "sales") {
      api.get("/reports/sales/categories").then(r => setCategories(r.data));
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "sales") {
      setSalesLoading(true);
      const q = categoryId ? `&categoryId=${categoryId}` : "";
      api.get(`/reports/sales?period=${period}${q}`)
        .then(r => setSales(r.data))
        .finally(() => setSalesLoading(false));
    }
  }, [tab, period, categoryId]);

  useEffect(() => {
    if (tab === "earnings") {
      setEarnLoading(true);
      api.get(`/reports/earnings?period=${period}`)
        .then(r => { setEarn(r.data); setOpenEmp(r.data?.employees?.length === 1 ? r.data.employees[0].userId : null); })
        .finally(() => setEarnLoading(false));
    }
  }, [tab, period]);

  useEffect(() => {
    if (tab === "staff") {
      setStaffLoading(true);
      const q = staffFilter ? `&assigneeId=${staffFilter}` : "";
      api.get(`/reports/staff?period=${period}${q}`)
        .then(r => setStaff(r.data))
        .finally(() => setStaffLoading(false));
    }
  }, [tab, period, staffFilter]);

  useEffect(() => {
    if (!printAll) return;
    const t = setTimeout(() => {
      const label = TABS.find(x => x.key === tab)?.label ?? "Отчёт";
      const per = PERIODS.find(x => x.key === period)?.label ?? "";
      printHtml(`Отчёт: ${label}`, contentRef.current?.innerHTML ?? "", `Период: ${per}.`);
      setPrintAll(false);
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printAll]);

  const toggle = (id: number) => setExpanded(prev => prev === id ? null : id);

  const TABS: { key: Tab; label: string; show: boolean }[] = [
    { key: "overview",   label: "Сводка",             show: !!perms.canSeeFinanceReports },
    { key: "finance",    label: "Финансы / P&L",      show: !!perms.canSeeFinanceReports },
    { key: "warehouse",  label: "Склад / Закупы",     show: !!perms.canSeeFinanceReports },
    { key: "halyk",      label: "Halyk Market",       show: !!perms.canSeeFinanceReports },
    { key: "sales",      label: "Продажи по товарам", show: !!perms.canSeeFinanceReports },
    { key: "earnings",   label: "Заработок",           show: !!perms.canSeeEarnings },
    { key: "staff",      label: "Сотрудники / Наряды", show: !!perms.canSeeStaffReport },
  ];

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h1 className="reports-title">Отчёты</h1>
      </div>

      {/* Вкладки */}
      <div className="reports-tabs">
        {TABS.filter(t => t.show).map(t => (
          <button key={t.key}
            className={`reports-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {/* Период */}
      <div className="reports-period-bar">
        {PERIODS.map(p => (
          <button key={p.key} className={`period-btn ${period === p.key ? "active" : ""}`} onClick={() => setPeriod(p.key)}>
            {p.label}
          </button>
        ))}
        <button className="period-btn" style={{ marginLeft: "auto" }} onClick={() => setPrintAll(true)}>
          🖨 Распечатать подробно
        </button>
      </div>

      <div ref={contentRef}>

      {/* ── СВОДКА ───────────────────────────────────────────────── */}
      {tab === "overview" && (
        <>
          {overviewLoading && <div className="reports-loading">Загрузка…</div>}
          {!overviewLoading && overview && (
            <>
              <div className="report-cards">
                <Card label="Сделок закрыто"  value={fmtN(overview.totalDeals)} accent />
                <Card label="Выручка"          value={fmt(overview.totalRevenue)} />
                <Card label="Себестоимость"    value={`−${fmt(overview.totalMontCost)}`} negative />
                <Card label="Комиссия Halyk"   value={`−${fmt(overview.totalHalykComm)}`} negative />
                <Card label="Уведомления"      value={`−${fmt(overview.totalNotifCost)}`} negative />
                <Card label="Постоян. расходы" value={`−${fmt(overview.totalFixed)}`} negative />
                <Card label="Налог 10%"        value={`−${fmt(overview.totalTax)}`} negative />
                <Card label="Чистая прибыль"   value={fmt(overview.totalNetProfit)} profit />
              </div>

              {overview.totalRevenue > 0 && (
                <div className="report-margin">
                  Маржа: <strong>{pct(overview.totalNetProfit / overview.totalRevenue * 100)}</strong>
                  &nbsp;·&nbsp;Средний чек: <strong>{fmt(overview.totalRevenue / overview.totalDeals)}</strong>
                </div>
              )}

              <div className="section-title">Разбивка по каналам продаж</div>
              {overview.channels.length > 0 ? (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Канал</th>
                        <th className="num">Сделок</th>
                        <th className="num">Выручка</th>
                        <th className="num">Себест.</th>
                        <th className="num">Комиссия</th>
                        <th className="num">Уведомл.</th>
                        <th className="num">Налог</th>
                        <th className="num profit-col">Прибыль</th>
                        <th className="num">Доля</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.channels.map(ch => (
                        <tr key={ch.id} className="deal-row">
                          <td><span className="order-code">{ch.name}</span></td>
                          <td className="num">{ch.dealsCount}</td>
                          <td className="num">{fmt(ch.revenue)}</td>
                          <td className="num neg">{ch.montCost > 0 ? `−${fmt(ch.montCost)}` : "—"}</td>
                          <td className="num neg">{ch.commission > 0 ? `−${fmt(ch.commission)}` : "—"}</td>
                          <td className="num neg">{ch.notifCost > 0 ? `−${fmt(ch.notifCost)}` : "—"}</td>
                          <td className="num neg">−{fmt(ch.tax)}</td>
                          <td className={`num profit-col ${ch.netProfit >= 0 ? "pos" : "neg"}`}>{fmt(ch.netProfit)}</td>
                          <td className="num">
                            <div className="bar-wrap">
                              <div className="bar-fill" style={{ width: `${Math.round(ch.revenue / overview.totalRevenue * 100)}%` }} />
                              <span>{Math.round(ch.revenue / overview.totalRevenue * 100)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, borderTop: "2px solid #374151" }}>
                        <td style={{ padding: "10px 12px", color: "#9ca3af", fontSize: 12 }}>ИТОГО</td>
                        <td className="num">{overview.totalDeals}</td>
                        <td className="num">{fmt(overview.totalRevenue)}</td>
                        <td className="num neg">−{fmt(overview.totalMontCost)}</td>
                        <td className="num neg">−{fmt(overview.totalHalykComm)}</td>
                        <td className="num neg">−{fmt(overview.totalNotifCost)}</td>
                        <td className="num neg">−{fmt(overview.totalTax)}</td>
                        <td className={`num profit-col ${overview.totalNetProfit >= 0 ? "pos" : "neg"}`}>{fmt(overview.totalNetProfit)}</td>
                        <td className="num">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="report-empty">Нет закрытых сделок за выбранный период</div>
              )}
            </>
          )}
        </>
      )}

      {/* ── ФИНАНСЫ / P&L ────────────────────────────────────────── */}
      {tab === "finance" && (
        <>
          {financeLoading && <div className="reports-loading">Загрузка…</div>}
          {!financeLoading && finance && (
            <div className="finance-layout">
              {/* Левая колонка — P&L */}
              <div className="finance-pl">
                <div className="pl-section">
                  <div className="pl-section-title">Поступления</div>
                  <PLRow label="Выручка (все каналы)" value={finance.totalRevenue} />
                  <PLRow label="Сделок закрыто"       value={finance.totalDeals} isCount />
                  <PLRow label="Средний чек"           value={finance.avgTicket} />
                  <PLRow label="В воронке (активные)"  value={finance.pipelineRevenue} muted />
                </div>

                <div className="pl-section">
                  <div className="pl-section-title">Себестоимость</div>
                  <PLRow label="Закуп товаров (MONT)"  value={-finance.totalMontCost} negative />
                  <PLRow label="Валовая прибыль"       value={finance.grossProfit} bold />
                  <PLRow label="Валовая маржа"         value={finance.grossMargin} isPct />
                </div>

                <div className="pl-section">
                  <div className="pl-section-title">Операционные расходы</div>
                  <PLRow label="Комиссия Halyk Market"   value={-finance.totalHalykComm} negative />
                  <PLRow label="Уведомления WhatsApp/SMS" value={-finance.totalNotifCost} negative />
                  <PLRow label="Уведомления клиентам (40₸/заявку)" value={-finance.notifFixed} negative />
                  <PLRow label="Сервер (100₸/заявку)"             value={-finance.serverFixed} negative />
                  <PLRow label="Телефония (126₸/заявку)"          value={-finance.phoneFixed} negative />
                  <PLRow label="Итого операц. расходы"  value={-finance.totalFixed - finance.totalHalykComm - finance.totalNotifCost} negative bold />
                </div>

                <div className="pl-section">
                  <div className="pl-section-title">Прибыль и налог</div>
                  <PLRow label="EBIT (до налога)"      value={finance.ebit} bold />
                  <PLRow label="Налог 10% (ИП/ТОО)"   value={-finance.tax} negative />
                  <PLRow label="Чистая прибыль"        value={finance.netProfit} profit bold />
                  <PLRow label="Чистая маржа"          value={finance.netMargin} isPct />
                </div>
              </div>

              {/* Правая колонка — доп. метрики */}
              <div className="finance-right">
                <div className="finance-kpis">
                  <KPI label="Чистая прибыль" value={fmt(finance.netProfit)} color={finance.netProfit >= 0 ? "#4ade80" : "#f87171"} />
                  <KPI label="Чистая маржа"   value={pct(finance.netMargin)} color={finance.netMargin >= 10 ? "#4ade80" : finance.netMargin >= 0 ? "#facc15" : "#f87171"} />
                  <KPI label="Валовая маржа"  value={pct(finance.grossMargin)} />
                  <KPI label="Средний чек"    value={fmt(finance.avgTicket)} />
                  <KPI label="Сделок закрыто" value={fmtN(finance.totalDeals)} />
                  <KPI label="Проигранных"    value={fmtN(finance.lostDealsCount)} color="#f87171" />
                  <KPI label="В работе"       value={fmtN(finance.activeDealsCount)} />
                  <KPI label="В воронке"      value={fmt(finance.pipelineRevenue)} />
                </div>

                {/* Расходы-пирог (CSS bar chart) */}
                {finance.totalRevenue > 0 && (
                  <div className="expense-breakdown">
                    <div className="pl-section-title" style={{ marginBottom: 12 }}>Структура выручки</div>
                    <ExpenseBar label="Закуп MONT"      amount={finance.totalMontCost}     total={finance.totalRevenue} color="#f87171" />
                    <ExpenseBar label="Комиссия Halyk"  amount={finance.totalHalykComm}    total={finance.totalRevenue} color="#fb923c" />
                    <ExpenseBar label="Пост. расходы"   amount={finance.totalFixed}        total={finance.totalRevenue} color="#facc15" />
                    <ExpenseBar label="Уведомления HM"  amount={finance.totalNotifCost}    total={finance.totalRevenue} color="#a78bfa" />
                    <ExpenseBar label="Налог 10%"       amount={finance.tax}               total={finance.totalRevenue} color="#60a5fa" />
                    <ExpenseBar label="Чистая прибыль"  amount={finance.netProfit}         total={finance.totalRevenue} color="#4ade80" profit />
                  </div>
                )}

                {/* Тренд по месяцам */}
                {finance.monthlyTrend.length > 1 && (
                  <div className="monthly-trend">
                    <div className="pl-section-title" style={{ marginBottom: 12 }}>Выручка по месяцам</div>
                    <MonthChart rows={finance.monthlyTrend} />
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── СКЛАД / ЗАКУПЫ ───────────────────────────────────────── */}
      {tab === "warehouse" && (
        <>
          {warehouseLoading && <div className="reports-loading">Загрузка…</div>}
          {!warehouseLoading && warehouse && (
            <>
              <div className="report-cards">
                <Card label="Приходов (без KEYOFF)" value={String(warehouse.receiptsCount)} accent />
                <Card label="Сумма закупа"           value={fmt(warehouse.totalReceiptAmount)} />
                <Card label="Закуплено единиц"       value={fmtN(warehouse.totalReceiptQty)} />
                <Card label="Списаний (документов)"  value={String(warehouse.issuesCount)} negative />
                <Card label="Сумма списаний"         value={fmt(warehouse.totalIssueAmount)} negative />
                <Card label="Списано единиц"         value={fmtN(warehouse.totalIssueQty)} negative />
              </div>

              <div className="wh-layout">
                {/* Поставщики */}
                <div className="wh-section">
                  <div className="section-title">Закупки по поставщикам</div>
                  {warehouse.suppliers.length > 0 ? (
                    <table className="report-table">
                      <thead><tr>
                        <th>Поставщик</th>
                        <th className="num">Документов</th>
                        <th className="num">Сумма</th>
                        <th className="num">Доля</th>
                      </tr></thead>
                      <tbody>
                        {warehouse.suppliers.map((s, i) => (
                          <tr key={i} className="deal-row">
                            <td><span className="order-code">{s.name}</span></td>
                            <td className="num">{s.docsCount}</td>
                            <td className="num">{fmt(s.amount)}</td>
                            <td className="num">
                              <div className="bar-wrap">
                                <div className="bar-fill" style={{ width: `${Math.round(s.amount / warehouse.totalReceiptAmount * 100)}%`, background: "rgba(250,204,21,0.6)" }} />
                                <span>{Math.round(s.amount / warehouse.totalReceiptAmount * 100)}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 700, borderTop: "2px solid #374151" }}>
                          <td style={{ padding: "10px 12px", color: "#9ca3af", fontSize: 12 }}>ИТОГО</td>
                          <td className="num">{warehouse.receiptsCount}</td>
                          <td className="num">{fmt(warehouse.totalReceiptAmount)}</td>
                          <td className="num">100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : <div className="report-empty">Нет приходов за период</div>}
                </div>

                {/* Топ товаров */}
                <div className="wh-section">
                  <div className="section-title">Топ закупаемых товаров</div>
                  {warehouse.topReceiptProducts.length > 0 ? (
                    <table className="report-table">
                      <thead><tr>
                        <th>Товар</th>
                        <th className="num">Кол-во</th>
                        <th className="num">Сумма</th>
                      </tr></thead>
                      <tbody>
                        {warehouse.topReceiptProducts.map((p, i) => (
                          <tr key={i} className="deal-row">
                            <td>
                              <div style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.name}>{p.name}</div>
                              {p.sku && <div className="cell-sub">{p.sku}</div>}
                            </td>
                            <td className="num">{fmtN(p.qty)}</td>
                            <td className="num">{fmt(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div className="report-empty">Нет данных</div>}

                  {/* Списания по причинам */}
                  {warehouse.issueReasons.length > 0 && (
                    <>
                      <div className="section-title" style={{ marginTop: 20 }}>Списания по причинам</div>
                      <table className="report-table">
                        <thead><tr>
                          <th>Причина</th>
                          <th className="num">Документов</th>
                          <th className="num">Ед.</th>
                          <th className="num">Сумма</th>
                        </tr></thead>
                        <tbody>
                          {warehouse.issueReasons.map((r, i) => (
                            <tr key={i} className="deal-row">
                              <td>{r.reason}</td>
                              <td className="num">{r.docsCount}</td>
                              <td className="num neg">{fmtN(r.qty)}</td>
                              <td className="num neg">−{fmt(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── HALYK ─────────────────────────────────────────────────── */}
      {tab === "halyk" && (
        <>
          {halykLoading && <div className="reports-loading">Загрузка…</div>}
          {!halykLoading && halyk && (
            <>
              <div className="report-cards">
                <Card label="Заказов" value={String(halyk.dealsCount)} accent />
                <Card label="Выручка" value={fmt(halyk.revenue)} />
                <Card label={`Комиссия Halyk (${halyk.commissionRate}%)`} value={`−${fmt(halyk.totalCommission)}`} negative />
                <Card label="Закуп MONT" value={`−${fmt(halyk.totalMontCost)}`} negative />
                <Card label="Уведомления" value={`−${fmt(halyk.totalNotifCost)}`} negative />
                <Card label="До налога" value={fmt(halyk.totalPreTax)} />
                <Card label="Налог 10%" value={`−${fmt(halyk.totalTax)}`} negative />
                <Card label="Чистая прибыль" value={fmt(halyk.totalNetProfit)} profit />
              </div>
              {halyk.revenue > 0 && (
                <div className="report-margin">
                  Маржа: <strong>{pct(halyk.totalNetProfit / halyk.revenue * 100)}</strong>
                  &nbsp;·&nbsp;Средний чек: <strong>{fmt(halyk.revenue / halyk.dealsCount)}</strong>
                </div>
              )}
              {halyk.deals.length > 0 ? (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Заказ</th><th>Клиент</th><th>Товар</th>
                        <th className="num">Сумма</th><th className="num">Комиссия</th>
                        <th className="num">MONT</th><th className="num">Уведомл.</th>
                        <th className="num">Налог</th><th className="num profit-col">Прибыль</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {halyk.deals.map(d => (
                        <>
                          <tr key={d.dealId} className={`deal-row ${(expanded === d.dealId || printAll) ? "expanded" : ""}`} onClick={() => toggle(d.dealId)}>
                            <td>
                              <span className="order-code">{d.orderCode || d.dealNumber}</span>
                              <span className="order-date">{new Date(d.paidAt).toLocaleDateString("ru-RU")}</span>
                            </td>
                            <td><div>{d.clientName}</div><div className="cell-sub">{d.clientPhone}</div></td>
                            <td className="product-cell">{d.products}</td>
                            <td className="num">{fmt(d.amount)}</td>
                            <td className="num neg">−{fmt(d.commission)}</td>
                            <td className="num neg">{d.montCost > 0 ? `−${fmt(d.montCost)}` : "—"}</td>
                            <td className="num neg"><span title={d.notifChannel}>−{fmt(d.notifCost)}</span></td>
                            <td className="num neg">−{fmt(d.tax)}</td>
                            <td className={`num profit-col ${d.netProfit >= 0 ? "pos" : "neg"}`}>{fmt(d.netProfit)}</td>
                            <td className="expand-btn">{expanded === d.dealId ? "▲" : "▼"}</td>
                          </tr>
                          {(expanded === d.dealId || printAll) && (
                            <tr key={`exp-${d.dealId}`} className="detail-row">
                              <td colSpan={10}>
                                <div className="detail-grid">
                                  <div className="detail-block">
                                    <div className="detail-label">Формула</div>
                                    <div className="detail-line">Выручка: <b>{fmt(d.amount)}</b></div>
                                    <div className="detail-line neg">− Комиссия {d.commissionRate}%: <b>{fmt(d.commission)}</b></div>
                                    <div className="detail-line neg">− Закуп MONT: <b>{fmt(d.montCost)}</b></div>
                                    <div className="detail-line neg">− Уведомление ({d.notifChannel}): <b>{fmt(d.notifCost)}</b></div>
                                    <div className="detail-line">= До налога: <b>{fmt(d.preTax)}</b></div>
                                    <div className="detail-line neg">− Налог 10%: <b>{fmt(d.tax)}</b></div>
                                    <div className="detail-line profit">= Чистая прибыль: <b>{fmt(d.netProfit)}</b></div>
                                  </div>
                                  {d.activationKeys?.length > 0 && (
                                    <div className="detail-block">
                                      <div className="detail-label">Ключи активации</div>
                                      {d.activationKeys.map((k, i) => <div key={i} className="key-chip">{k}</div>)}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="report-empty">Нет завершённых заказов за выбранный период</div>
              )}
            </>
          )}
        </>
      )}

      {/* ── ПРОДАЖИ ПО ТОВАРАМ ────────────────────────────────────── */}
      {tab === "sales" && (
        <>
          <div style={{ padding: "0 0 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>Категория:</label>
            <select
              className="period-btn"
              style={{ padding: "7px 14px", borderRadius: 8, background: "#1f2937", border: "1px solid #374151", color: "#f9fafb", fontSize: 13, cursor: "pointer", flex: "1 1 200px", maxWidth: 320 }}
              value={categoryId}
              onChange={e => setCategoryId(e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">— Все категории —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {salesLoading && <div className="reports-loading">Загрузка…</div>}

          {!salesLoading && sales && (
            <>
              <div className="report-cards">
                <Card label="Позиций продано"        value={String(sales.totals.qty)} accent />
                <Card label="Выручка"                value={fmt(sales.totals.revenue)} />
                <Card label="Комиссия 10%"           value={`−${fmt(sales.totals.commission)}`} negative />
                <Card label="Расходы (266₸/заявку)"  value={`−${fmt(sales.totals.fixedCosts)}`} negative />
                <Card label="До налога"              value={fmt(sales.totals.preTax)} />
                <Card label="Налог 10%"              value={`−${fmt(sales.totals.tax)}`} negative />
                <Card label="Себестоимость"          value={`−${fmt(sales.totals.cost)}`} negative />
                <Card label="Чистая прибыль"         value={fmt(sales.totals.profit)} profit />
              </div>

              {sales.totals.revenue > 0 && (
                <div className="report-margin">
                  Маржа: <strong>{pct(sales.totals.profit / sales.totals.revenue * 100)}</strong>
                  &nbsp;·&nbsp;Расходы/заявку: <strong>уведомл. 40₸ + сервер 100₸ + тел. 126₸ = 266₸</strong>
                </div>
              )}

              {sales.rows.length > 0 ? (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Товар</th>
                        <th>Категория</th>
                        <th className="num">Заявок</th>
                        <th className="num">Кол-во</th>
                        <th className="num">Выручка</th>
                        <th className="num">Комис.</th>
                        <th className="num">Расходы</th>
                        <th className="num">Налог</th>
                        <th className="num">Себест.</th>
                        <th className="num profit-col">Прибыль</th>
                        <th className="num">Маржа</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.rows.map(r => (
                        <tr key={r.productId} className="deal-row">
                          <td style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.nameRu}>{r.nameRu}</td>
                          <td><span className="cell-sub">{r.category}</span></td>
                          <td className="num">{r.dealsCount}</td>
                          <td className="num">{r.qty}</td>
                          <td className="num">{fmt(r.revenue)}</td>
                          <td className="num neg">−{fmt(r.commission)}</td>
                          <td className="num neg">−{fmt(r.fixedCosts)}</td>
                          <td className="num neg">−{fmt(r.tax)}</td>
                          <td className="num neg">{r.cost > 0 ? `−${fmt(r.cost)}` : "—"}</td>
                          <td className={`num profit-col ${r.profit >= 0 ? "pos" : "neg"}`}>{fmt(r.profit)}</td>
                          <td className="num">{r.revenue > 0 ? pct(r.profit / r.revenue * 100) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, borderTop: "2px solid #374151" }}>
                        <td colSpan={2} style={{ padding: "10px 12px", color: "#9ca3af", fontSize: 12 }}>ИТОГО</td>
                        <td className="num">{sales.rows.reduce((s, r) => s + r.dealsCount, 0)}</td>
                        <td className="num">{sales.totals.qty}</td>
                        <td className="num">{fmt(sales.totals.revenue)}</td>
                        <td className="num neg">−{fmt(sales.totals.commission)}</td>
                        <td className="num neg">−{fmt(sales.totals.fixedCosts)}</td>
                        <td className="num neg">−{fmt(sales.totals.tax)}</td>
                        <td className="num neg">{sales.totals.cost > 0 ? `−${fmt(sales.totals.cost)}` : "—"}</td>
                        <td className={`num profit-col ${sales.totals.profit >= 0 ? "pos" : "neg"}`}>{fmt(sales.totals.profit)}</td>
                        <td className="num">{sales.totals.revenue > 0 ? pct(sales.totals.profit / sales.totals.revenue * 100) : "—"}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="report-empty">Нет данных за выбранный период</div>
              )}
            </>
          )}
        </>
      )}

      {tab === "earnings" && (
        <>
          {earnLoading && <div className="reports-loading">Загрузка…</div>}
          {!earnLoading && earn && (
            <>
              <div className="report-cards">
                <Card label="К выплате (итого)" value={fmt(earn.totalPayout)} accent />
                <Card label="Завершено сделок" value={fmtN(earn.dealsCount)} />
                <Card label="Сотрудников" value={String(earn.employees.length)} />
                {earn.canSeeCost && earn.unassigned > 0 && <Card label="Сделок без ответственного" value={String(earn.unassigned)} negative />}
                {earn.firmTotals && <Card label="Выручка" value={fmt(earn.firmTotals.revenue)} />}
                {earn.firmTotals && <Card label="Налог (внутри остатка)" value={fmt(earn.firmTotals.tax)} />}
                {earn.firmTotals && <Card label="Остаток в фирме" value={fmt(earn.firmTotals.firmLeft)} profit />}
              </div>

              {(earn.days ?? []).length > 0 && (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>День</th><th className="num">Сделок</th>
                        {earn.canSeeCost && <th className="num">Выручка</th>}
                        <th className="num">Товары</th><th className="num">Услуги</th><th className="num">Соисполнители</th>
                        <th className="num">Выплаты</th>
                        {earn.canSeeCost && <th className="num">Налог</th>}
                        {earn.canSeeCost && <th className="num">Остаток в фирме</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {earn.days.map((d: any) => (
                        <tr key={d.date}>
                          <td><b>{new Date(d.date + "T00:00:00").toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", weekday: "short" })}</b></td>
                          <td className="num">{d.dealsCount}</td>
                          {earn.canSeeCost && <td className="num">{fmtN(d.revenue)}</td>}
                          <td className="num">{fmtN(d.goods)}</td>
                          <td className="num">{fmtN(d.services)}</td>
                          <td className="num">{fmtN(d.co)}</td>
                          <td className="num"><b>{fmtN(d.payout)}</b></td>
                          {earn.canSeeCost && <td className="num">{fmtN(d.tax)}</td>}
                          {earn.canSeeCost && <td className="num profit-col pos">{fmtN(d.firmLeft)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {earn.canSeeCost && <div className="earn-rules">Остаток в фирме = оплата клиента − закуп − выплаты сотрудникам. Закуп делается с предоплаты клиента, поэтому в остаток он не входит. Налог лежит внутри остатка: его нужно отложить.</div>}

              <div className="earn-rules">
                {earn.canSeeCost ? (
                  <>
                    Ответственный: {Math.round(earn.rates.goods * 100)}% с товаров и {Math.round(earn.rates.services * 100)}% с услуг.
                    Соисполнитель: {Math.round(earn.rates.coServices * 100)}% с услуг. Выплаты считаются от дохода (цена − закуп, включая закуп от KEYOFF) за вычетом налога; налог в {Math.round(earn.rates.tax * 100)}% берётся с дохода без закупа от KEYOFF. Себестоимость — по последнему закупу.
                  </>
                ) : (
                  <>Заработок считается по завершённым сделкам: ответственный — {Math.round(earn.rates.goods * 100)}% с товаров и {Math.round(earn.rates.services * 100)}% с услуг, соисполнитель — {Math.round(earn.rates.coServices * 100)}% с услуг.</>
                )}
              </div>

              {earn.employees.length === 0 && <div className="report-empty">За выбранный период нет завершённых сделок</div>}

              {earn.employees.map((e: any) => {
                const open = printAll || openEmp === e.userId;
                return (
                  <div key={e.userId} className="earn-emp">
                    <div className="earn-emp-head" onClick={() => setOpenEmp(open && !printAll ? null : e.userId)}>
                      <div className="earn-emp-name">
                        {e.name}
                        <span className="cell-sub"> · {e.dealsCount} сдел.</span>
                      </div>
                      <div className="earn-emp-total">{fmt(e.total)}</div>
                      <div className="earn-emp-split">
                        {e.goods > 0 && <span>Товары {fmt(e.goods)}</span>}
                        {e.services > 0 && <span>Услуги {fmt(e.services)}</span>}
                        {e.co > 0 && <span>Соисполнитель {fmt(e.co)}</span>}
                      </div>
                    </div>
                    {open && (
                      <div className="report-table-wrap">
                        <table className="report-table">
                          <thead>
                            <tr>
                              <th>Сделка</th><th>Клиент</th><th>Роль</th>
                              {earn.canSeeCost && <><th className="num">Доход товары</th><th className="num">Доход услуги</th><th className="num">Чистая товары</th><th className="num">Чистая услуги</th></>}
                              <th className="num">Заработок</th>
                              {earn.canSeeCost && <th className="num">Налог</th>}
                        {earn.canSeeCost && <th className="num">Остаток в фирме</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {e.deals.map((d: any, i: number) => (
                              <tr key={d.dealId + "-" + i}>
                                <td><b>{d.number}</b><div className="cell-sub">{new Date(d.doneAt).toLocaleDateString("ru-RU")} · {d.title}</div></td>
                                <td>{d.client}</td>
                                <td>{d.role === "co" ? "соисполнитель" : "ответственный"}</td>
                                {earn.canSeeCost && <><td className="num">{fmtN(d.goodsIncome)}</td><td className="num">{fmtN(d.servicesIncome)}</td><td className="num">{fmtN(d.goodsNet)}</td><td className="num">{fmtN(d.servicesNet)}</td></>}
                                <td className="num profit-col pos">{fmt(d.payout)}</td>
                                {earn.canSeeCost && <td className="num">{fmtN(d.tax)}</td>}
                                {earn.canSeeCost && <td className="num">{fmtN(d.firmLeft)}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}

      {tab === "staff" && (
        <>
          {staffLoading && <div className="reports-loading">Загрузка…</div>}
          {!staffLoading && staff && (
            <>
              <div className="report-cards">
                <Card label="Закрыто нарядов" value={String(staff.totalOrders)} accent />
                <Card label="Исполнителей" value={String(staff.staff.length)} />
              </div>
              {staff.staff.length > 0 && (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead><tr><th>Сотрудник</th><th className="num">Нарядов</th><th className="num">Работ</th><th className="num">Часов</th><th>Видео</th><th>Ремонт ПК</th><th>Прочее</th></tr></thead>
                    <tbody>
                      {staff.staff.map((s: any) => (
                        <tr key={s.assigneeId ?? 0} style={{ cursor: "pointer" }} onClick={() => setStaffFilter(staffFilter === s.assigneeId ? "" : (s.assigneeId ?? ""))}>
                          <td>{s.name}{s.coOrders > 0 && <div className="cell-sub">из них соисполнитель: {s.coOrders}</div>}</td><td className="num">{s.ordersCount}</td><td className="num">{s.worksCount}</td>
                          <td className="num">{s.hours || "—"}</td><td>{s.byKind.video}</td><td>{s.byKind.pc_repair}</td><td>{s.byKind.other}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {staff.orders.length > 0 ? (
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead><tr><th>Наряд</th><th>Исполнитель</th><th>Клиент / объект</th><th>Что сделано</th><th>Материалы</th><th>Закрыт</th></tr></thead>
                    <tbody>
                      {staff.orders.map((o: any) => (
                        <tr key={o.id}>
                          <td><b>{o.number}</b><div className="cell-sub">{o.title}</div></td>
                          <td>{o.assignee}{(o.coAssignees ?? []).length > 0 && <div className="cell-sub">+ {o.coAssignees.join(", ")}</div>}</td>
                          <td>{o.client}<div className="cell-sub">{o.address || ""}</div></td>
                          <td>
                            {o.works.map((w: any, i: number) => <div key={i}>{w.done === false ? "☐" : "☑"} {w.name}{w.qty ? ` — ${w.qty}${w.unit ? " " + w.unit : ""}` : ""}</div>)}
                            {o.resultNote && <div className="cell-sub">Итог: {o.resultNote}</div>}
                          </td>
                          <td>{o.materials.map((m: any, i: number) => <div key={i}>{m.name} — {m.qty}{m.unit ? " " + m.unit : ""}</div>)}</td>
                          <td>{o.completedAt ? new Date(o.completedAt).toLocaleString("ru-RU") : "—"}{o.hours != null && <div className="cell-sub">{o.hours} ч</div>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="report-empty">Нет закрытых нарядов за выбранный период</div>
              )}
            </>
          )}
        </>
      )}
      </div>
    </div>
  );
}

// ── Компоненты ──────────────────────────────────────────────────────

function Card({ label, value, accent, negative, profit }: {
  label: string; value: string; accent?: boolean; negative?: boolean; profit?: boolean;
}) {
  return (
    <div className={`report-card ${accent ? "accent" : ""} ${negative ? "card-neg" : ""} ${profit ? "card-profit" : ""}`}>
      <div className="card-label">{label}</div>
      <div className="card-value">{value}</div>
    </div>
  );
}

function PLRow({ label, value, negative, profit, bold, muted, isCount, isPct }: {
  label: string; value: number; negative?: boolean; profit?: boolean;
  bold?: boolean; muted?: boolean; isCount?: boolean; isPct?: boolean;
}) {
  const formatted = isPct
    ? pct(value)
    : isCount
    ? fmtN(value)
    : fmt(Math.abs(value));
  const sign = negative && value !== 0 ? "−" : "";
  const cls = `pl-row${profit ? " pl-profit" : negative ? " pl-neg" : ""}${bold ? " pl-bold" : ""}${muted ? " pl-muted" : ""}`;
  return (
    <div className={cls}>
      <span className="pl-label">{label}</span>
      <span className="pl-val">{sign}{formatted}</span>
    </div>
  );
}

function KPI({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

function ExpenseBar({ label, amount, total, color, profit }: {
  label: string; amount: number; total: number; color: string; profit?: boolean;
}) {
  const p = total > 0 ? Math.max(0, Math.round(Math.abs(amount) / total * 100)) : 0;
  return (
    <div className="expense-bar-row">
      <div className="expense-bar-label">{label}</div>
      <div className="expense-bar-track">
        <div className="expense-bar-fill" style={{ width: `${p}%`, background: color, opacity: profit ? 1 : 0.7 }} />
      </div>
      <div className="expense-bar-pct" style={{ color }}>{p}%</div>
      <div className="expense-bar-amt" style={{ color: profit ? color : undefined }}>
        {profit && amount < 0 ? "−" : ""}{fmt(Math.abs(amount))}
      </div>
    </div>
  );
}

function MonthChart({ rows: allRows }: { rows: MonthlyTrend[] }) {
  // записи без корректного месяца (например, «unknown») на графике не показываем
  const rows = allRows.filter(r => /^\d{4}-\d{2}$/.test(r.month));
  const max = Math.max(1, ...rows.map(r => r.revenue));
  const monthNames = ["Янв","Фев","Мар","Апр","Май","Июн","Июл","Авг","Сен","Окт","Ноя","Дек"];
  return (
    <div className="month-chart">
      {rows.map(r => {
        const [y, m] = r.month.split("-");
        const name = monthNames[parseInt(m) - 1] + " " + y.slice(2);
        const h = max > 0 ? Math.round(r.revenue / max * 80) : 0;
        return (
          <div key={r.month} className="month-bar-col" title={`${name}: ${fmt(r.revenue)}`}>
            <div className="month-bar-val">{Math.round(r.revenue / 1000)}к</div>
            <div className="month-bar-body" style={{ height: `${h}px` }} />
            <div className="month-bar-label">{name}</div>
          </div>
        );
      })}
    </div>
  );
}
